import type { AgriMaterialStockInNote } from '@/lib/api/agri-material-lot-api'
import type { PackagingLotStockInNote } from '@/lib/api/packaging-lot-api'
import type { ProductLotStockInNote } from '@/lib/api/product-lot-api'
import type { ScentingLotStockInNote } from '@/lib/api/scenting-lot-api'
import type { SortingLotStockInNote } from '@/lib/api/sorting-lot-api'
import type {
    StockInNotePrintData,
    StockInNotePrintRow,
} from '@/lib/stock-in-note-print'

/** Kho chè mua / phân loại / lấy hương cân theo kg và API phiếu không trả đơn
 *  vị — mặc định 'kg' giống cách các bảng lô đang hiển thị. */
const TEA_UNIT = 'kg'

export interface StockInNotePrintContext {
    warehouseLabel: string
    /** Lý do nhập đã dịch — mỗi kho có bộ lý do riêng. */
    reasonLabel: string
    statusLabel: string
}

/** Phần đầu phiếu giống hệt nhau ở cả 6 kho. */
interface StockInNoteHeaderLike {
    code: string
    notes?: string | null
    created_by_name?: string
    approved_by_name?: string
    created_at: string
}

function baseData(
    note: StockInNoteHeaderLike,
    ctx: StockInNotePrintContext,
): Omit<StockInNotePrintData, 'rows'> {
    return {
        warehouseLabel: ctx.warehouseLabel,
        noteCode: note.code,
        reasonLabel: ctx.reasonLabel,
        statusLabel: ctx.statusLabel,
        createdAt: note.created_at,
        createdByName: note.created_by_name ?? '',
        approvedByName: note.approved_by_name ?? '',
        notes: note.notes ?? '',
    }
}

export function productLotInNoteToPrintData(
    note: ProductLotStockInNote,
    ctx: StockInNotePrintContext,
): StockInNotePrintData {
    const rows: StockInNotePrintRow[] = (note.items ?? []).map((item) => ({
        lotCode: item.product_lot_code ?? '',
        name: item.product_name ?? '',
        quantity: item.quantity,
        unit: item.product_unit ?? '',
        position: item.position_code ?? '',
    }))
    return { ...baseData(note, ctx), rows }
}

export function sortingLotInNoteToPrintData(
    note: SortingLotStockInNote,
    ctx: StockInNotePrintContext,
): StockInNotePrintData {
    const rows: StockInNotePrintRow[] = (note.items ?? []).map((item) => ({
        lotCode: item.sorting_lot_code ?? '',
        name: item.grade_name ?? '',
        quantity: item.quantity,
        unit: TEA_UNIT,
        position: '',
    }))
    return { ...baseData(note, ctx), rows }
}

export function scentingLotInNoteToPrintData(
    note: ScentingLotStockInNote,
    ctx: StockInNotePrintContext,
): StockInNotePrintData {
    const rows: StockInNotePrintRow[] = (note.items ?? []).map((item) => ({
        lotCode: item.scenting_lot_code ?? '',
        name: item.grade_name ?? '',
        quantity: item.quantity,
        unit: TEA_UNIT,
        position: '',
    }))
    return { ...baseData(note, ctx), rows }
}

export function packagingLotInNoteToPrintData(
    note: PackagingLotStockInNote,
    ctx: StockInNotePrintContext,
): StockInNotePrintData {
    // API phiếu kho bao bì chưa trả đơn vị vật tư → cột ĐVT để trống.
    const rows: StockInNotePrintRow[] = (note.items ?? []).map((item) => ({
        lotCode: item.packaging_lot_code ?? '',
        name: item.material_name ?? '',
        quantity: item.quantity,
        unit: '',
        position: item.position_code ?? '',
    }))
    return { ...baseData(note, ctx), rows }
}

export function agriMaterialInNoteToPrintData(
    note: AgriMaterialStockInNote,
    ctx: StockInNotePrintContext,
): StockInNotePrintData {
    const rows: StockInNotePrintRow[] = (note.items ?? []).map((item) => ({
        lotCode: item.lot_code ?? '',
        name: item.material_name ?? '',
        quantity: item.quantity,
        unit: item.material_unit ?? '',
        position: '',
    }))
    return { ...baseData(note, ctx), rows }
}
